import { useMemo } from 'react';
import { useFetchData } from '../hooks/useFetchData';
import {
  getMockWorkflowDistribution,
} from './mockDemoData';
import { formatSignalLabelFromKey, getSignalVariant } from '../utils/signalUtils';

// ── normalizers ────────────────────────────────────────────────────────────

function normalizeV2Service(svc) {
  if (!svc) return null;
  const s = svc.summary || {};
  const metric = (key) => s.metrics?.[key] || {};
  const metricCurrent = (key, fallback) => metric(key).current ?? fallback ?? null;
  const metricDelta = (key, fallback = null) => metric(key).delta ?? fallback;
  return {
    app_id: String(svc.service_id),
    app_name: svc.service_name,
    kpis: {
      activity: {
        total_traces: metricCurrent('trace_count', s.trace_count),
        new_traces_1h: metricDelta('trace_count'),
        traces_delta_previous_period: metricDelta('trace_count'),
        total_sessions: metricCurrent('session_count', s.session_count),
        new_sessions_1h: metricDelta('session_count'),
        sessions_delta_previous_period: metricDelta('session_count'),
      },
      latency: {
        p95_total: metricCurrent('p95_ms', s.p95_ms),
        p95_1h: metricDelta('p95_ms'),
        p95_delta_previous_period: metricDelta('p95_ms'),
      },
      cost: {
        total_cost_usd: metricCurrent('cost_usd', s.cost_usd),
        delta_cost_1h_usd: metricDelta('cost_usd'),
        delta_cost_previous_period_usd: metricDelta('cost_usd'),
      },
      classifiers: {
        positive: { total: null, delta_1h: null },
        negative: {
          total: metricCurrent('classifiers_negative_total', s.classifiers_negative_total),
          delta_1h: metricDelta('classifiers_negative_total'),
          delta_previous_period: metricDelta('classifiers_negative_total'),
        },
        agentic: { total: null, delta_1h: null },
        safety: { total: null, delta_1h: null },
      },
      detections: {
        errors: s.classifier_error_total ?? null,
      },
    },
  };
}

// ── hook ───────────────────────────────────────────────────────────────────

/**
 * Fetch single-app KPI data for the App Overview tab.
 *
 * V2: GET /v2/data/services/{appId}?env=...&range=...&summary=true
 *     Returns service snapshot with range-scoped KPI summary block.
 * @param {string|number} appId - Service ID from route params.
 */
/**
 * Fetch workflow outcome data for the App Overview Signals section.
 *
 * GET /v2/data/services/{appId}/workflows?env=...&range=...
 * Returns workflows with trace_count, success_count, failure_count, pending_count.
 *
 * @param {string|number|null} appId - Pass null to disable.
 */
/**
 * Fetch entity name lists for filter dropdowns.
 *
 * GET /v2/data/services/{appId}/entities?env=...&range=...
 * Returns { agents, tools, models, workflows, sessions, users } as string arrays.
 * Sessions/users are time-scoped; agents/tools/models/workflows are lifetime lists.
 *
 * @param {string|number|null} appId - Pass null to disable.
 */
/**
 * Fetch per-agent cost/token time series.
 *
 * GET /v2/data/services/{appId}/usage/agents?env=...&range=...
 * Returns flat rows: { bucket_time, agent_id, agent_name, cost_usd, input_tokens, ... }
 *
 * @param {string|number|null} appId - Pass null to disable.
 */
export function useUsageAgents(appId) {
  const endpoint = appId ? `/v2/data/services/${appId}/usage/agents` : null;
  const { data, loading, error } = useFetchData({ endpoint, enabled: !!appId });
  const rows = useMemo(() => (Array.isArray(data?.data) ? data.data : null), [data]);
  return { rows, loading, error };
}

/**
 * Fetch per-model cost/token time series.
 *
 * GET /v2/data/services/{appId}/usage/models?env=...&range=...
 * Returns flat rows: { bucket_time, model_name, llm_provider, cost_usd, input_tokens, ... }
 *
 * @param {string|number|null} appId - Pass null to disable.
 */
export function useUsageModels(appId) {
  const endpoint = appId ? `/v2/data/services/${appId}/usage/models` : null;
  const { data, loading, error } = useFetchData({ endpoint, enabled: !!appId });
  const rows = useMemo(() => (Array.isArray(data?.data) ? data.data : null), [data]);
  return { rows, loading, error };
}

/**
 * Fetch per-workflow cost/token aggregates.
 *
 * GET /v2/data/services/{appId}/usage/workflows?env=...&range=...
 * Returns flat rows: { workflow_id, canonical_name, cost_usd, trace_count, ... }
 *
 * @param {string|number|null} appId - Pass null to disable.
 */
export function useUsageWorkflows(appId) {
  const endpoint = appId ? `/v2/data/services/${appId}/usage/workflows` : null;
  const { data, loading, error } = useFetchData({ endpoint, enabled: !!appId });
  const rows = useMemo(() => (Array.isArray(data?.data) ? data.data : null), [data]);
  return { rows, loading, error };
}

export function useEntities(appId) {
  const endpoint = appId ? `/v2/data/services/${appId}/entities` : null;
  const enabled = !!appId;

  const { data, loading, error } = useFetchData({ endpoint, enabled });

  const entities = useMemo(() => {
    if (!data?.data) return null;
    const d = data.data;
    return {
      agents:    Array.isArray(d.agents)    ? d.agents    : [],
      tools:     Array.isArray(d.tools)     ? d.tools     : [],
      models:    Array.isArray(d.models)    ? d.models    : [],
      workflows: Array.isArray(d.workflows) ? d.workflows : [],
      sessions:  Array.isArray(d.sessions)  ? d.sessions  : [],
      users:     Array.isArray(d.users)     ? d.users     : [],
    };
  }, [data]);

  return { entities, loading, error };
}

export function useWorkflowOutcomes(appId) {
  const endpoint = appId ? `/v2/data/services/${appId}/workflows` : null;
  const enabled = !!appId;

  const { data, loading, error } = useFetchData({ endpoint, enabled });

  const workflows = useMemo(() => {
    if (!data) return [];
    return (Array.isArray(data.data) ? data.data : []).map(wf => ({
      workflow_id: wf.workflow_id,
      canonical_name: wf.canonical_name,
      trace_count: wf.trace_count ?? 0,
      success_count: wf.success_count ?? 0,
      failure_count: wf.failure_count ?? 0,
      pending_count: wf.pending_count ?? 0,
      last_activity: wf.last_activity ?? null,
    }));
  }, [data]);

  return { workflows, loading, error };
}

const workflowSignalsFromCounts = (signalCounts) => {
  if (!signalCounts || typeof signalCounts !== 'object') {
    return { signalCount: 0, negativeSignalCount: 0, topSignals: [] };
  }

  const topSignals = Object.entries(signalCounts)
    .map(([key, rawCount]) => {
      const count = Number(rawCount || 0);
      const variant = getSignalVariant(null, key);
      return {
        key,
        label: formatSignalLabelFromKey(key) || key,
        count: Number.isFinite(count) ? count : 0,
        polarity: variant === 'info' ? 'positive' : 'negative',
      };
    })
    .filter(signal => signal.key && signal.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return {
    signalCount: topSignals.reduce((sum, signal) => sum + signal.count, 0),
    negativeSignalCount: topSignals
      .filter(signal => signal.polarity !== 'positive')
      .reduce((sum, signal) => sum + signal.count, 0),
    topSignals,
  };
};

/**
 * Fetch workflow distribution for the Overview signals section.
 *
 * V2: GET /v2/data/services/{appId}/workflows?env=...&range=...
 *     Returns per-workflow rows with canonical_name, trace_count, success_count,
 *     failure_count. One distribution is derived client-side:
 *       - workflowDistribution  — per workflow, sorted by failure_count desc
 *
 * @param {string|number|null} appId
 * @param {{ environment, isDemo, sessionToken, timeWindow }} opts
 */
export function useWorkflowDistribution(appId, { environment, isDemo, sessionToken, timeWindow } = {}) {
  const v2Endpoint = (!isDemo && appId) ? `/v2/data/services/${appId}/workflows` : null;
  const { data: v2Data, loading: v2Loading, error: v2FetchError } = useFetchData({
    endpoint: v2Endpoint,
    enabled:  !!v2Endpoint,
  });

  const v2Rows = useMemo(
    () => (v2Data?.data && Array.isArray(v2Data.data) ? v2Data.data : null),
    [v2Data],
  );

  const activeV2Rows = useMemo(() => {
    if (!v2Rows) return null;
    return v2Rows.filter(wf => Number(wf?.trace_count || 0) > 0);
  }, [v2Rows]);

  const workflowDistributionV2 = useMemo(() => {
    if (!activeV2Rows) return [];
    return activeV2Rows
      .filter(wf => wf.canonical_name)
      .map(wf => {
        const signalSummary = workflowSignalsFromCounts(wf.signal_counts);
        return {
          workflowId:          wf.workflow_id,
          workflowVersion:     wf.workflow_version ?? 1,
          workflowName:        wf.canonical_name,
          canonical_name:      wf.canonical_name,
          total:               wf.trace_count   ?? 0,
          positiveCount:       wf.success_count ?? 0,
          negativeCount:       wf.failure_count ?? 0,
          pendingCount:        wf.pending_count ?? 0,
          lastActivity:        wf.last_activity ?? null,
          ...signalSummary,
        };
      })
      .sort((a, b) => b.negativeCount - a.negativeCount || b.total - a.total);
  }, [activeV2Rows]);

  // Return mock data for demo apps
  if (isDemo && appId) {
    return {
      workflowDistribution: getMockWorkflowDistribution(appId),
      loading: false,
      error:   null,
    };
  }

  return {
    workflowDistribution: workflowDistributionV2,
    loading: v2Loading,
    error:   v2FetchError ? 'Unable to load business task distribution right now.' : null,
  };
}

export function useAppOverview(appId) {
  const endpoint = appId ? `/v2/data/services/${appId}` : null;
  const params = { summary: 'true' };
  const enabled = !!appId;

  const { data, loading, error } = useFetchData({ endpoint, params, enabled });

  const appData = useMemo(() => {
    if (!data) return null;
    return normalizeV2Service(data.data);
  }, [data]);

  return { appData, loading, error };
}

/**
 * Fetch the last-1h activity totals for the Explore delta line.
 *
 * Same summary endpoint as useAppOverview but with range='1h' (the param
 * overrides the global range in useFetchData). The 1h totals ARE the deltas
 * shown as "(+N last 1h)" — no subtraction. Env still comes from context.
 */
export function useActivity1h(appId) {
  const endpoint = appId ? `/v2/data/services/${appId}` : null;
  const params = { summary: 'true', range: '1h' };
  const enabled = !!appId;

  const { data } = useFetchData({ endpoint, params, enabled });

  return useMemo(() => {
    const a = data ? normalizeV2Service(data.data)?.kpis?.activity : null;
    return {
      traces1h:   a?.total_traces   ?? null,
      sessions1h: a?.total_sessions ?? null,
    };
  }, [data]);
}
